import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
  selector: '[appStatusDrawerQuillKeyboard]',
  standalone: true
})
export class StatusDrawerQuillKeyboardDirective {
  private focusEnteredWithTab = false;

  constructor(private readonly hostRef: ElementRef<HTMLElement>) {}

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    this.focusEnteredWithTab = event.key === 'Tab';
  }

  @HostListener('document:mousedown')
  onDocumentMousedown(): void {
    this.focusEnteredWithTab = false;
  }

  @HostListener('document:touchstart')
  onDocumentTouchstart(): void {
    this.focusEnteredWithTab = false;
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isFromQuillEditor(event.target)) {
      return;
    }

    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      this.handleSubmitShortcut(event);
    }
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    if (!this.focusEnteredWithTab) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const editor = target.closest('.ql-editor');
    if (!(editor instanceof HTMLElement)) {
      return;
    }

    // Keep keyboard focus in the editor but collapse selection at the end
    // so existing content is not highlighted when tabbing into Quill.
    requestAnimationFrame(() => this.placeCaretAtEnd(editor));
  }

  private isFromQuillEditor(target: EventTarget | null): target is HTMLElement {
    return target instanceof HTMLElement && !!target.closest('.ql-editor');
  }

  private handleTabNavigation(event: KeyboardEvent): void {
    const scope = this.resolveNavigationScope();
    const focusableElements = this.getFocusableElements(scope);
    if (!focusableElements.length) {
      return;
    }

    event.preventDefault();

    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const direction = event.shiftKey ? -1 : 1;
    const anchorElement = this.resolveAnchorElement(event.target, activeElement);
    const currentElement = this.resolveCurrentElement(anchorElement, focusableElements);
    const currentIndex = currentElement ? focusableElements.indexOf(currentElement) : -1;

    if (currentIndex === -1 && anchorElement) {
      const relativeIndex = this.findRelativeFocusableIndex(anchorElement, focusableElements, direction);
      if (relativeIndex !== -1) {
        focusableElements[relativeIndex]?.focus();
        return;
      }
    }

    let nextIndex = currentIndex + direction;

    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : focusableElements.length - 1;
    } else if (nextIndex < 0) {
      nextIndex = focusableElements.length - 1;
    } else if (nextIndex >= focusableElements.length) {
      nextIndex = 0;
    }

    focusableElements[nextIndex]?.focus();
  }

  private handleSubmitShortcut(event: KeyboardEvent): void {
    const form = this.resolveFormElement(event.target);
    if (!form) {
      return;
    }

    event.preventDefault();

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  private resolveNavigationScope(): HTMLElement {
    const host = this.hostRef.nativeElement;
    const scope =
      host.closest('.side-drawer') ??
      host.closest('.mat-mdc-dialog-surface') ??
      host.closest('form') ??
      host;

    return scope as HTMLElement;
  }

  private resolveFormElement(target: EventTarget | null): HTMLFormElement | null {
    const targetElement = target instanceof HTMLElement ? target : null;
    const form = targetElement?.closest('form') ?? this.hostRef.nativeElement.closest('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  private getFocusableElements(scope: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(',');

    const candidates = Array.from(scope.querySelectorAll<HTMLElement>(selector));
    return candidates.filter((element) => this.isFocusable(element));
  }

  private isFocusable(element: HTMLElement): boolean {
    if (element.closest('.ql-toolbar') || element.closest('.ql-picker-options')) {
      return false;
    }

    // Skip only tree row containers so Tab from Quill lands on tree controls
    // (checkboxes/toggles) instead of jumping past the entire tree section.
    if (element.matches('mat-tree-node, .mat-tree-node, .cdk-tree-node, [role="treeitem"]')) {
      return false;
    }

    if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    if (element.closest('[aria-hidden="true"]')) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    const isQuillEditor = element.classList.contains('ql-editor') || element.getAttribute('contenteditable') === 'true';
    if (element.tabIndex < 0 && !isQuillEditor) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0;
  }

  private resolveAnchorElement(target: EventTarget | null, activeElement: HTMLElement | null): HTMLElement | null {
    if (target instanceof HTMLElement) {
      const editor = target.closest('.ql-editor');
      if (editor instanceof HTMLElement) {
        return editor;
      }
      return target;
    }
    return activeElement;
  }

  private resolveCurrentElement(anchor: HTMLElement | null, focusableElements: HTMLElement[]): HTMLElement | null {
    if (!anchor) {
      return null;
    }

    if (focusableElements.includes(anchor)) {
      return anchor;
    }

    const containingCandidate = focusableElements.find((candidate) => candidate.contains(anchor));
    if (containingCandidate) {
      return containingCandidate;
    }

    const containedCandidate = focusableElements.find((candidate) => anchor.contains(candidate));
    return containedCandidate ?? null;
  }

  private findRelativeFocusableIndex(
    anchor: HTMLElement,
    focusableElements: HTMLElement[],
    direction: 1 | -1
  ): number {
    if (direction > 0) {
      for (let index = 0; index < focusableElements.length; index++) {
        const candidate = focusableElements[index];
        const relation = anchor.compareDocumentPosition(candidate);
        if ((relation & Node.DOCUMENT_POSITION_FOLLOWING) !== 0) {
          return index;
        }
      }
      return -1;
    }

    for (let index = focusableElements.length - 1; index >= 0; index--) {
      const candidate = focusableElements[index];
      const relation = anchor.compareDocumentPosition(candidate);
      if ((relation & Node.DOCUMENT_POSITION_PRECEDING) !== 0) {
        return index;
      }
    }

    return -1;
  }

  private placeCaretAtEnd(editor: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}
